import { c as _c } from "react/compiler-runtime";
import type { CountryRecord } from '@onefootprint/core';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryFromCode, getDefaultCountry } from '@onefootprint/core';
import type { CreateDocumentRequest, CreateDocumentResponse, Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type { RadioSelectOptionFields } from '@onefootprint/ui';
import { Button, Divider, Form, LinkButton, RadioSelect } from '@onefootprint/ui';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useRequestErrorToast from '@/idv2/features/collect-document/hooks/use-request-error-toast';
import type { IdDocOutcome, SupportedIdDocType } from '@/idv2/features/collect-document/types';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import NavigationHeader from '../../../../components/navigation-header';
import StickyBottomBox from '../../../../components/sticky-bottom-box';
import useSubmitDocType from '../../../../hooks/use-submit-doc-type';
import { isMobileKind } from '../../../../utils/capture';
import detectWebcam from '../../../utils/detect-webcam';
import useOptionsByDocType from '../use-options-by-doc-type';
type FormData = {
  country: Iso3166TwoDigitCountryCode;
  docType: `${SupportedIdDocType}`;
};
type IdDocCountryAndTypeContainerProps = {
  onSubmitDocTypeSuccess: (data: CreateDocumentResponse, country: CountryRecord, docType: `${SupportedIdDocType}`) => void;
  userCountryCode?: Iso3166TwoDigitCountryCode;
  authToken: string;
  device: DeviceInfo;
  supportedCountryAndDocTypes: Partial<Record<Iso3166TwoDigitCountryCode, SupportedIdDocType[]>>;
  sandboxOutcome?: IdDocOutcome;
  selectedCountryCode?: Iso3166TwoDigitCountryCode;
  selectedDocType?: `${SupportedIdDocType}`;
  documentRequestId: string;
  onContinueOnMobile?: () => void;
};
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'id-doc-country-and-type-container'
});
const getRecordKeys = <T extends object,>(object: T) => Object.keys(object) as (keyof T)[];
const sortDocTypes = (types: SupportedIdDocType[]) => {
  // rearrange supportedDocumentTypes to make sure the first three are drivers license, passport, id card if they are supported
  types.sort((a, b) => {
    const order = ['drivers_license', 'passport', 'id_card'];
    if (order.includes(a) && order.includes(b)) {
      return order.indexOf(a) - order.indexOf(b);
    }
    if (order.includes(a)) {
      return -1;
    }
    if (order.includes(b)) {
      return 1;
    }
    return 0;
  });
};
const IdDocCountryAndTypeContainer = t0 => {
  const $ = _c(88);
  const {
    onSubmitDocTypeSuccess,
    userCountryCode,
    selectedCountryCode,
    selectedDocType,
    authToken,
    device,
    supportedCountryAndDocTypes,
    sandboxOutcome,
    documentRequestId,
    onContinueOnMobile
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "document-flow.id-doc.pages.country-and-type-selection"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const requestErrorToast = useRequestErrorToast();
  const submitDocTypeMutation = useSubmitDocType();
  const {
    isPending: isDocTypeSubmissionLoading
  } = submitDocTypeMutation;
  const {
    type: deviceType
  } = device;
  let supportedCountryRecords;
  let t2;
  if ($[1] !== selectedCountryCode || $[2] !== supportedCountryAndDocTypes || $[3] !== userCountryCode) {
    const supportedCountries = new Set(getRecordKeys(supportedCountryAndDocTypes));
    supportedCountryRecords = COUNTRIES.filter(country => supportedCountries.has(country.value));
    t2 = getDefaultCountry(supportedCountries, selectedCountryCode || userCountryCode);
    $[1] = selectedCountryCode;
    $[2] = supportedCountryAndDocTypes;
    $[3] = userCountryCode;
    $[4] = supportedCountryRecords;
    $[5] = t2;
  } else {
    supportedCountryRecords = $[4];
    t2 = $[5];
  }
  const defaultSupportedCountry = t2;
  const defaultCountryCode = defaultSupportedCountry?.value ?? DEFAULT_COUNTRY.value;
  let types;
  if ($[6] !== defaultCountryCode || $[7] !== supportedCountryAndDocTypes) {
    types = [...(supportedCountryAndDocTypes[defaultCountryCode] ?? [])];
    sortDocTypes(types);
    $[6] = defaultCountryCode;
    $[7] = supportedCountryAndDocTypes;
    $[8] = types;
  } else {
    types = $[8];
  }
  const firstTypeFromOptions = types.length ? types[0] : "passport";
  const t3 = selectedDocType ?? firstTypeFromOptions;
  let t4;
  if ($[9] !== defaultCountryCode || $[10] !== t3) {
    t4 = {
      defaultValues: {
        country: defaultCountryCode,
        docType: t3
      }
    };
    $[9] = defaultCountryCode;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const formMethods = useForm(t4);
  const {
    setValue,
    handleSubmit,
    control
  } = formMethods;
  let t5;
  if ($[12] !== control) {
    t5 = {
      control,
      name: "country"
    };
    $[12] = control;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const watchedCountry = useWatch(t5);
  let t6;
  if ($[14] !== control) {
    t6 = {
      control,
      name: "docType"
    };
    $[14] = control;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const watchedDocType = useWatch(t6);
  let t7;
  if ($[16] !== setValue || $[17] !== supportedCountryAndDocTypes) {
    t7 = countryCode => {
      const typesForNextCountry = [...(supportedCountryAndDocTypes[countryCode] ?? [])];
      sortDocTypes(typesForNextCountry);
      const nextType = typesForNextCountry.length ? typesForNextCountry[0] : "passport";
      setValue("docType", nextType);
    };
    $[16] = setValue;
    $[17] = supportedCountryAndDocTypes;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleCountryChange = t7;
  let t8;
  if ($[19] !== setValue) {
    t8 = value => {
      setValue("docType", value as SupportedIdDocType);
    };
    $[19] = setValue;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleDocTypeChange = t8;
  let t9;
  if ($[21] !== authToken || $[22] !== deviceType || $[23] !== documentRequestId || $[24] !== onSubmitDocTypeSuccess || $[25] !== requestErrorToast || $[26] !== sandboxOutcome || $[27] !== submitDocTypeMutation) {
    t9 = async data => {
      const hasWebcam = await detectWebcam();
      if (submitDocTypeMutation.isPending) {
        return;
      }
      const countryRecord = getCountryFromCode(data.country);
      if (!countryRecord) {
        logWarn(`Invalid country code: ${data.country}`);
        return;
      }
      logInfo(`submitting doc type - ${data.docType} and country - ${data.country}, hasWebcam: ${hasWebcam}`);
      submitDocTypeMutation.mutate({
        body: {
          documentType: data.docType as CreateDocumentRequest['documentType'],
          countryCode: data.country,
          fixtureResult: sandboxOutcome,
          deviceType: isMobileKind(deviceType) ? "mobile" : "desktop",
          skipSelfie: !hasWebcam,
          requestId: documentRequestId
        },
        headers: {
          "X-Fp-Authorization": authToken
        }
      }, {
        onSuccess: responseData => {
          onSubmitDocTypeSuccess(responseData, countryRecord, data.docType);
        },
        onError: err => {
          logWarn(`Failed to submit doc type and country. Selected doctype: ${data.docType}, country ${data.country}. Error:`, err);
          requestErrorToast(err);
        }
      });
    };
    $[21] = authToken;
    $[22] = deviceType;
    $[23] = documentRequestId;
    $[24] = onSubmitDocTypeSuccess;
    $[25] = requestErrorToast;
    $[26] = sandboxOutcome;
    $[27] = submitDocTypeMutation;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const onSubmit = t9;
  let currentTypes;
  if ($[29] !== supportedCountryAndDocTypes || $[30] !== watchedCountry) {
    currentTypes = [...(supportedCountryAndDocTypes[watchedCountry] ?? [])];
    sortDocTypes(currentTypes);
    $[29] = supportedCountryAndDocTypes;
    $[30] = watchedCountry;
    $[31] = currentTypes;
  } else {
    currentTypes = $[31];
  }
  const optionsByDocType = useOptionsByDocType(currentTypes);
  let t10;
  if ($[32] !== currentTypes || $[33] !== optionsByDocType) {
    t10 = currentTypes.map(type => optionsByDocType[type]).filter(_temp);
    $[32] = currentTypes;
    $[33] = optionsByDocType;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const options = t10;
  let t11;
  if ($[35] !== device.type) {
    t11 = isMobileKind(device.type) ? undefined : {
      variant: "close",
      confirmClose: true
    };
    $[35] = device.type;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t11) {
    t12 = <NavigationHeader leftButton={t11} />;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t) {
    t13 = t(_temp2);
    $[39] = t;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t13) {
    t14 = <h2 className="text-heading-3 text-primary">{t13}</h2>;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t) {
    t15 = t(_temp3);
    $[43] = t;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t15) {
    t16 = <p className="text-body-2 text-secondary">{t15}</p>;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t14 || $[48] !== t16) {
    t17 = <div className="flex flex-col items-center gap-2 text-center">{t14}{t16}</div>;
    $[47] = t14;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t) {
    t18 = t(_temp4);
    $[50] = t;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  const t19 = supportedCountryRecords.length <= 1;
  let t20;
  if ($[52] !== supportedCountryRecords.length || $[53] !== t || $[54] !== watchedCountry) {
    t20 = supportedCountryRecords.length === 1 ? t(_temp5, {
      country: getCountryFromCode(watchedCountry)?.label
    }) : undefined;
    $[52] = supportedCountryRecords.length;
    $[53] = t;
    $[54] = watchedCountry;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== handleCountryChange || $[57] !== supportedCountryRecords || $[58] !== t18 || $[59] !== t19 || $[60] !== t20) {
    t21 = <Form.Country name="country" label={t18} disabled={t19} options={supportedCountryRecords} onChange={handleCountryChange} hint={t20} testID="country-selector" />;
    $[56] = handleCountryChange;
    $[57] = supportedCountryRecords;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Divider />;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== handleDocTypeChange || $[64] !== options || $[65] !== optionsByDocType || $[66] !== t || $[67] !== watchedDocType) {
    t23 = options.length > 0 ? <RadioSelect value={optionsByDocType[watchedDocType]?.value} options={options} onChange={handleDocTypeChange} testID="doc-selector" size="compact" /> : <div className="text-body-2 text-center mx-4 text-primary">{t(_temp6)}</div>;
    $[63] = handleDocTypeChange;
    $[64] = options;
    $[65] = optionsByDocType;
    $[66] = t;
    $[67] = watchedDocType;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t21 || $[70] !== t23) {
    t24 = <div className="flex flex-col gap-4 justify-center items-center w-full [&>div]:w-full">{t21}{t22}{t23}</div>;
    $[69] = t21;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== device.type || $[73] !== handleSubmit || $[74] !== isDocTypeSubmissionLoading || $[75] !== onContinueOnMobile || $[76] !== onSubmit || $[77] !== options.length || $[78] !== t) {
    t25 = options.length > 0 && <StickyBottomBox><form className="w-full flex flex-col gap-3 items-center" onSubmit={handleSubmit(onSubmit)}><Button type="submit" fullWidth={true} loading={isDocTypeSubmissionLoading} size="large" data-dd-action-name="country-doc:continue">{t(_temp7)}</Button>{!isMobileKind(device.type) && onContinueOnMobile && <LinkButton onClick={onContinueOnMobile}>{t(_temp8)}</LinkButton>}</form></StickyBottomBox>;
    $[72] = device.type;
    $[73] = handleSubmit;
    $[74] = isDocTypeSubmissionLoading;
    $[75] = onContinueOnMobile;
    $[76] = onSubmit;
    $[77] = options.length;
    $[78] = t;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== t12 || $[81] !== t17 || $[82] !== t24 || $[83] !== t25) {
    t26 = <div className="flex flex-col gap-6 justify-center items-center">{t12}{t17}{t24}{t25}</div>;
    $[80] = t12;
    $[81] = t17;
    $[82] = t24;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== formMethods || $[86] !== t26) {
    t27 = <FormProvider {...formMethods}>{t26}</FormProvider>;
    $[85] = formMethods;
    $[86] = t26;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  return t27;
};
export default IdDocCountryAndTypeContainer;
function _temp(option) {
  return !!option;
}
function _temp2($) {
  return $.title;
}
function _temp3($_0) {
  return $_0.subtitle;
}
function _temp4($_1) {
  return $_1.form.country;
}
function _temp5($_2) {
  return $_2.form.hint.label;
}
function _temp6($_3) {
  return $_3.form["not-supported"];
}
function _temp7($_4) {
  return $_4.form.cta;
}
function _temp8($_5) {
  return $_5.form["continue-on-mobile"];
}